import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { LegalizationDocumentSummaryDto } from '../../models/dtos/legalization-document-summary-dto';
import { ScreenService } from 'src/app/core/screen.service';
import { LegalizationFileDto } from '../../models/legalization-file-dto';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { LegalizationAllDocumentsListDataSource } from './legalization-all-documents-list-datasource';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Filter } from '../../models/common/filter';
import { firstValueFrom, merge, Subject, Subscription, tap } from 'rxjs';
import { NgxSpinnerService } from 'ngx-spinner';
import { TranslateService } from '@ngx-translate/core';
import { LegalizationFilesModalComponent, LegalizationFilesModalData } from '../legalization-files-modal/legalization-files-modal.component';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { AddLegalizationInformationModalData } from '../save-info-modals/AddLegalizationInformationModalData';
import { SaveLegalizationInfoByCoordinatorComponent } from '../save-info-modals/save-legalization-info-by-coordinator/save-legalization-info-by-coordinator.component';
import { SaveLegalizationInfoByLegalizerModalComponent } from '../save-info-modals/save-legalization-info-by-legalizer/save-legalization-info-by-legalizer-modal.component';
import { LegalizationDocumentStatusEnum } from 'src/app/models/enums/legalization-document-status-enum';
import {
  EditLegalizationDocumentModalComponent,
  EditLegalizationDocumentModalData,
} from '../save-info-modals/components/edit-legalization-document/edit-legalization-document-modal.component';
import { AssignLegalizationDocumentComponent } from '../assign-legalization-document/assign-legalization-document.component';
import { AssignLegalizationDocumentData } from '../save-info-modals/AssignLegalizationDocumentData';

@Component({
  selector: 'app-legalization-all-documents-list',
  templateUrl: './legalization-all-documents-list.component.html',
  styleUrls: ['./legalization-all-documents-list.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
    trigger('rowExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0', display: 'none' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  standalone: false
})
export class LegalizationAllDocumentsListComponent implements OnInit {
  screenService = inject(ScreenService);
  dataSource = inject(LegalizationAllDocumentsListDataSource);
  private spinner = inject(NgxSpinnerService);
  private translateService = inject(TranslateService);
  private dialog = inject(MatDialog);

  public readonly registerPrefix = 'Legalization.Register';

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  legalizationDocuments: LegalizationDocumentSummaryDto[] = [];
  expandedElementDocumentId: number | null = null;
  expansionDataName = 'PreviousVersionsEntites';
  hideInactive: boolean = false;
  correctDocumentLabel: string;
  addDocumentLabel: string;

  private readonly defaultPage: number = 1;
  private readonly defaultPageSize: number = 10;
  public readonly defaultSortColumn: string = 'createdOn';
  private readonly defaultSortDirection: string = 'desc';
  private filters: Filter[] = [];

  private loadingSubscription: Subscription;

  private readonly unsubscribe$ = new Subject<void>();

  displayedColumns = [
    'expandingIcon',
    'lastName',
    'firstName',
    'document',
    'documentStartDate',
    'documentCategoryName',
    'documentStatus',
    'alert',
    'legalizationStatus',
    'statusJustification',
    'actions',
  ];
  displayedExpandedColumns = [
    'expandingIconSpace',
    'lastName',
    'firstName',
    'document',
    'documentStartDate',
    'documentCategoryName',
    'documentStatus',
    'alert',
    'legalizationStatus',
    'statusJustification',
    'actions',
  ];

  constructor() { }

  ngOnInit(): void {
    this.loadingSubscription = this.dataSource.loading$.subscribe((isLoading) => {
      if (isLoading) {
        this.spinner.show();
      } else {
        this.spinner.hide();
      }
    });

    this.updateLabels();
  }

  ngAfterViewInit(): void {
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
    this.translateService.onLangChange.subscribe(() => {
      this.updateLabels();
      this.paginator.pageIndex = 0;
    });

    merge(this.sort.sortChange, this.paginator.page, this.translateService.onLangChange)
      .pipe(tap(() => this.fetchDocuments()))
      .subscribe();

    this.fetchDocuments();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();

    if (this.loadingSubscription) {
      this.loadingSubscription.unsubscribe();
    }
  }

  expandRow(expandedElementId, legalizationDocumentId) {
    this.expandedElementDocumentId = expandedElementId === legalizationDocumentId ? null : legalizationDocumentId;
  }

  onIncludeInactiveChange(event: any): void {
    this.hideInactive = event.checked;
    this.fetchDocuments();
  }

  public openLegalizationFilesModal(legalizationFiles: LegalizationFileDto[], workerId: number, documentTypeName: string): void {
    const dialogRef = this.dialog.open(
      LegalizationFilesModalComponent,
      this.dialogConfig(<LegalizationFilesModalData>{
        LegalizationFiles: legalizationFiles,
        WorkerId: workerId,
        DocumentTypeName: documentTypeName,
      }),
    );
  }

  openDocumentLegalizationForCoordinator(workerId: number): void {
    const dialogRef = this.dialog.open(
      SaveLegalizationInfoByCoordinatorComponent,
      this.dialogConfig(<AddLegalizationInformationModalData>{
        Title: 'Legalization.Title',
        WorkerId: workerId,
      }),
    );

    dialogRef.afterClosed().subscribe((result) => this.handleDialogResponseWithRefresh(result));
  }

  openDocumentLegalizationForLegalizer(workerId: number, leagalizationId: number, documentStatusId?: number, documentId?: number): void {
    let isCorrection = documentStatusId ? this.isToCorrection(documentStatusId) : false;

    const dialogRef = this.dialog.open(
      SaveLegalizationInfoByLegalizerModalComponent,
      this.dialogConfig(<AddLegalizationInformationModalData>{
        Title: 'Legalization.Title',
        PreSelectedLegalizationsIds: [leagalizationId],
        WorkerId: workerId,
        IsCorrection: isCorrection,
        LegalizationDocumentId: documentId
      }),
    );

    dialogRef.afterClosed().subscribe((result) => this.handleDialogResponseWithRefresh(result));
  }

  async editDocument(legalizationDocumentId: number): Promise<void> {
    const dialogRef = this.dialog.open(
      EditLegalizationDocumentModalComponent,
      this.dialogConfig(<EditLegalizationDocumentModalData>{
        LegalizationDocumentId: legalizationDocumentId,
      }),
    );

    const result = await firstValueFrom(dialogRef.afterClosed());

    if (result && result.isSuccess) {
      if (result.openAssignDocuments && result.workerData) {
        const assignDialogRef = this.dialog.open(
          AssignLegalizationDocumentComponent,
          this.dialogConfig(<AssignLegalizationDocumentData>{
            Title: this.translateService.instant('Legalization.AssignDocuments'),
            WorkerId: result.workerData.workerId,
            LegalizationDocumentId: result.workerData.legalizationDocumentId,
          }),
        );

        const assignResult = await firstValueFrom(assignDialogRef.afterClosed());
        this.handleDialogResponseWithRefresh(assignResult || { isSuccess: true });
      } else {
        this.handleDialogResponseWithRefresh(result);
      }
    }
  }

  isToCorrection(documentStatusId: number): boolean {
    return documentStatusId === LegalizationDocumentStatusEnum.RequiresCorrection;
  }

  addOrCorrectLabel(element: LegalizationDocumentSummaryDto): string {
    return this.isToCorrection(element.DocumentStatusId) ? this.correctDocumentLabel : this.addDocumentLabel;
  }

  private updateLabels() {
    this.correctDocumentLabel = this.translateService.instant(`${this.registerPrefix}.CorrectDocument`);
    this.addDocumentLabel = this.translateService.instant(`${this.registerPrefix}.AddDocument`);
  }

  private handleDialogResponseWithRefresh(result: any): void {
    if (!result) {
      return;
    }

    this.fetchDocuments();
  }

  private fetchDocuments(): void {
    try {
      this.dataSource.legalizationsSubject.next({
        Page: this.paginator.pageIndex + 1,
        PageSize: this.paginator.pageSize ?? this.defaultPageSize,
        SortingField: this.sort?.active ?? this.defaultSortColumn,
        SortingDirection: this.sort?.direction ?? this.defaultSortDirection,
        Filters: this.filters,
        IncludeInactive: !this.hideInactive,
      });
    } catch (error) {
      this.spinner.hide();
    }
  }

  private dialogConfig = <Type>(data: Type): MatDialogConfig<Type> => {
    const dialogConfig = new MatDialogConfig<Type>();
    dialogConfig.panelClass = 'legalization-form-dialog';
    dialogConfig.data = data;
    return dialogConfig;
  };
}
